import { useContext, useEffect, useState } from "react"
import { Dialog, DialogPanel, DialogBackdrop, Radio, RadioGroup } from "@headlessui/react"
import { AdjustmentsVerticalIcon, ChevronRightIcon } from "@heroicons/react/24/outline"
import { MultiContext } from "~/contexts/MultiContext"
import { classNames } from "~/utils/classNames"
import { ControlButton } from "~/components/ControlButton"
import { Preview } from "./Preview"
import { actions } from "./utils"

export function CommandMenuTrigger() {
  const { setIsCommandMenuOpen } = useContext(MultiContext)

  return (
    <div className="absolute left-0 right-0 top-0 m-4">
      <div className="flex justify-center gap-4">
        <ControlButton
          onClick={() => setIsCommandMenuOpen(true)}
          Icon={AdjustmentsVerticalIcon}
          tooltip={"Open Command Menu"}
          iconOverrides="text-gray-600 hover:text-gray-400"
        />
      </div>
    </div>
  )
}

export function CommandMenu() {
  const {
    isCommandMenuOpen,
    setIsCommandMenuOpen,
    isCommandMenuFocused,
    selectedCommandMenuOption,
    setSelectedCommandMenuOption,
  } = useContext(MultiContext)

  useEffect(() => {
    const shortcutKeys = actions.map(({ shortcut }) => shortcut && shortcut.toLowerCase())

    const down = (e) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setIsCommandMenuOpen((open) => !open)
      }

      if (isCommandMenuOpen && !isCommandMenuFocused && shortcutKeys.includes(e.key)) {
        e.preventDefault()
        setSelectedCommandMenuOption(actions.find((a) => a.shortcut === e.key)?.id)
      }
    }

    document.addEventListener("keydown", down)
    return () => document.removeEventListener("keydown", down)
  }, [isCommandMenuOpen, setIsCommandMenuOpen, isCommandMenuFocused, setSelectedCommandMenuOption])

  return (
    <Dialog
      className="relative z-10"
      open={isCommandMenuOpen}
      onClose={() => {
        setIsCommandMenuOpen(false)
        setSelectedCommandMenuOption(null)
      }}
    >
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500/25 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-10 mt-10 w-screen overflow-y-auto p-4 sm:mt-0 sm:p-6 md:p-20">
        <DialogPanel
          transition
          className="mx-auto max-w-3xl transform overflow-hidden rounded-xl bg-white/80 shadow-2xl ring-1 ring-black/5 backdrop-blur backdrop-filter transition-all data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
        >
          <RadioGroup
            value={selectedCommandMenuOption}
            onChange={(newOption) =>
              setSelectedCommandMenuOption((prevOption) => {
                if (newOption === "autofocus") {
                  if (prevOption === actions[0].id) {
                    document.getElementById(actions.at(-1).id).focus()
                    return actions.at(-1).id
                  } else {
                    document.getElementById(actions.at(0).id).focus()
                    return actions.at(0).id
                  }
                } else {
                  return newOption
                }
              })
            }
          >
            <div className="flex transform-gpu flex-col divide-x divide-y divide-gray-800/10 sm:flex-row">
              <div className="max-h-[32rem] min-w-0 flex-auto scroll-py-4 overflow-y-auto px-6 py-4 sm:h-[32-rem]">
                <div className="-mx-2 text-sm text-gray-700">
                  <Radio key="autofocus" value="autofocus" aria-hidden={true} autoFocus />
                  {actions.map((action) => (
                    <Radio
                      id={action.id}
                      key={action.id}
                      value={action.id}
                      aria-label={action.name}
                      className={classNames(
                        "group flex cursor-default select-none items-center rounded-md p-2 outline-none",
                        selectedCommandMenuOption === action.id
                          ? "bg-gray-900/10 text-gray-900"
                          : "hover:bg-gray-600/10"
                      )}
                    >
                      <action.icon
                        className="size-6 flex-none text-gray-900/40 group-data-[focus]:text-gray-900"
                        aria-hidden="true"
                      />
                      <span className="ml-3 flex-auto truncate">{action.name}</span>
                      {action.shortcut && (
                        <span
                          className={classNames(
                            "ml-3 flex-none text-xs font-semibold text-gray-400 group-data-[focus]:text-indigo-100",
                            selectedCommandMenuOption === action.id ? "hidden" : "block"
                          )}
                        >
                          <kbd className="font-sans">{action.shortcut.toUpperCase()}</kbd>
                        </span>
                      )}
                      <ChevronRightIcon
                        className={classNames(
                          "ml-3 size-5 flex-none text-gray-400",
                          selectedCommandMenuOption === action.id ? "block" : "hidden"
                        )}
                        aria-hidden="true"
                      />
                    </Radio>
                  ))}
                </div>
              </div>

              {selectedCommandMenuOption && <Preview option={selectedCommandMenuOption} />}
            </div>
          </RadioGroup>
        </DialogPanel>
      </div>
    </Dialog>
  )
}
